import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Package, PlusCircle, Mail, Layers, Settings, History, LogOut, Menu, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/spedizioni", label: "Spedizioni", icon: Package, end: true, id: "shipments" },
  { to: "/admin/spedizioni/nuova", label: "Nuova spedizione", icon: PlusCircle, id: "new-shipment" },
  { to: "/admin/messaggi", label: "Messaggi", icon: Mail, id: "messages" },
  { to: "/admin/servizi", label: "Servizi del sito", icon: Layers, id: "services" },
  { to: "/admin/registro", label: "Registro attività", icon: History, id: "audit" },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "settings" },
];

const SideNav = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex flex-col h-full bg-[#0B0C0E] text-slate-300">
      <Link to="/admin" onClick={onNavigate} className="px-4 pt-4"><img src="/logo.jpg" alt="LINCESSO" className="h-20 w-auto" /></Link>
      <div className="flag-bar h-[2px] mx-5 mt-2 opacity-70" />
      <nav className="flex-1 px-3 py-6 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`admin-nav-${n.id}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors border-l-2 ${isActive ? "bg-white/[0.06] text-white border-[#E50914]" : "border-transparent hover:bg-white/[0.04] hover:text-white"}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-500 hover:text-white border-l-2 border-transparent" data-testid="admin-nav-site"><ExternalLink className="h-4 w-4" /> Vai al sito</a>
      </nav>
      <div className="p-4 border-t border-white/10">
        <p className="text-xs text-slate-500">Connesso come</p>
        <p className="text-sm text-white truncate" data-testid="admin-user-email">{user?.email}</p>
        <button onClick={logout} className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-[#E50914] transition-colors" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Esci</button>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64" data-testid="admin-layout">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 border-r border-black"><SideNav /></aside>
      <header className="lg:hidden sticky top-0 z-40 bg-[#0B0C0E] flex items-center justify-between px-4 h-14">
        <img src="/logo.jpg" alt="LINCESSO" className="h-11 w-auto" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="text-white p-2" aria-label="Menu" data-testid="admin-mobile-menu-btn"><Menu className="h-6 w-6" /></button></SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 border-black bg-[#0B0C0E] text-white">
            <SheetTitle className="sr-only">Menu amministrazione</SheetTitle>
            <SideNav onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="p-4 sm:p-6 lg:p-10 max-w-[1500px]"><Outlet /></main>
    </div>
  );
}

export const PageTitle = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-900" data-testid="admin-page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
